<template>
	<header-block title="My Documents" :logicalBack="activeTab !== 'all'" @logicalBack="activeTab = 'all'" @whatsapp="onHeaderWhatsApp"></header-block>
	<div v-if="!(customer && documents)" class="mt-5">
		<v-skeleton-loader width="100%" type="paragraph" class="mb-4" />
		<v-row>
			<v-col><v-skeleton-loader width="100%" type="card" /></v-col>
			<v-col><v-skeleton-loader width="100%" type="card" /></v-col>
		</v-row>
		<v-row>
			<v-col><v-skeleton-loader width="100%" type="card" /></v-col>
			<v-col><v-skeleton-loader width="100%" type="card" /></v-col>
		</v-row>
		<v-row>
			<v-col><v-skeleton-loader width="100%" type="card" /></v-col>
			<v-col><v-skeleton-loader width="100%" type="card" /></v-col>
		</v-row>
	</div>
	<transition>
		<div v-if="customer && documents">

			<AiChatBanner />

			<!-- Empty state -->
			<template v-if="!hasDocuments">
				<!-- Hero Walkthrough Skeleton -->
				<div v-if="!heroWalkthroughData" class="walkthrough-section">
					<v-skeleton-loader type="card" height="240px" />
				</div>

				<!-- Hero Walkthrough -->
				<div v-if="heroWalkthroughData" class="walkthrough-section">
					<MGWalkthrough
						:walkthroughData="heroWalkthroughData"
						variant="sheet"
						:compact="true"
						:dotsAbove="false"
						:imageHeight="'220'"
						height="240px"
						@action-clicked="handleWhatsAppAction"
					/>
				</div>

				<p class="what-can-add-heading">What can I add?</p>
				<p class="what-can-add-subtext">Send documents via WhatsApp and we'll organise them for you</p>

				<DocumentTypeGrid class="document-type-grid-section" />

				<FaqsSection v-if="faqs.length" class="faq-section" :faqs="faqs" :limit="5" faqName="document-faqs" />

				<!-- WhatsApp Promo Card -->
				<WhatsAppPromoCard
					class="whatsapp-promo"
					ref="whatsAppPromoCard"
					:showImage="true"
					:closable="false"
					title="Send a document or photo"
					subtitle="We'll add the key details and save the document securely. You can also chat with your WhatsApp assistant about this policy."
					@action="handleWhatsAppAction"
				/>
			</template>

			<!-- Has documents -->
			<template v-else>
				<!-- Filter Tabs -->
				<AccountTypeFilter :tabs="filterTabs" v-model="activeTab" />

				<!-- Category view (All tab) -->
				<template v-if="activeTab === 'all'">
					<!-- Category Cards — 2-column grid -->
					<div class="category-grid">
						<DocCategoryCard
							v-for="card in categoryCards"
							:key="card.tabValue"
							:label="card.label"
							:illustration="card.illustration"
							:count="categoryCounts[card.tabValue] ? categoryCounts[card.tabValue] + ' documents' : null"
							@click="activeTab = card.tabValue"
						/>
					</div>

					<!-- Recently Added -->
					<RecentDocuments
						:documents="documents"
						:limit="3"
						@doc-click="doc => openDoc(doc.id)"
						@view-more="$router.push('/AllDocuments')"
					/>

					<FaqsSection v-if="faqs.length" class="faq-section" :faqs="faqs" :limit="5" faqName="document-faqs" />

					<!-- WhatsApp Promo Card -->
					<WhatsAppPromoCard
						class="whatsapp-promo"
						ref="whatsAppPromoCard"
						:showImage="true"
						:closable="false"
						title="Send a document or photo"
						subtitle="We'll add the key details and save the document securely. You can also chat with your WhatsApp assistant about this policy."
						@action="handleWhatsAppAction"
					/>
				</template>

				<!-- Filtered list view (non-All tabs) -->
				<template v-else>
					<template v-if="groupedDocuments.length === 0">
					<div class="no-filtered-docs">
						<p class="no-filtered-docs-title">No documents here yet.</p>
						<p class="no-filtered-docs-subtitle">Send one and we'll file it here automatically.</p>
					</div>

					<FaqsSection v-if="faqs.length" class="faq-section" :faqs="faqs" :limit="5" faqName="document-faqs" />

					<WhatsAppPromoCard
						class="whatsapp-promo"
						ref="whatsAppPromoCard"
						:showImage="true"
						:closable="false"
						title="Send a document or photo"
						subtitle="We'll add the key details and save the document securely. You can also chat with your WhatsApp assistant about this policy."
						@action="handleWhatsAppAction"
					/>
				</template>
					<template v-for="group in groupedDocuments" :key="group.label">
						<div class="doc-type-group">
							<p class="group-label">{{ group.label }}</p>
							<div
								v-for="doc in group.docs"
								:key="doc.id"
								class="doc-row"
								@click="openDoc(doc.id)"
							>
								<div class="doc-info">
									<p class="doc-company">{{ properCase(doc.company || doc.documentSubject) }}</p>
									<p class="doc-filename">{{ doc.documentName }}</p>
									<p class="doc-added">Added {{ doc.createDateTimeString }}</p>
									<p v-if="doc.documentSubject" class="doc-label">{{ doc.documentSubject }}</p>
								</div>
								<img :src="require('@/assets/arrow-right.png')" class="doc-arrow" />
							</div>
						</div>
					</template>
				</template>
			</template>

			<div style="clear:both; padding-bottom: 110px;"></div>
		</div>
	</transition>

	<WhatsAppEnableDrawer
		ref="whatsAppEnableDrawer"
		:phoneNumber="customer?.person?.mobilePhone || ''"
		@enable="handleEnableWhatsApp"
		@done="handleWhatsAppEnableDone"
		@closed="handleWhatsAppDrawerClosed"
	/>
</template>

<script>
import Services from '../mixins/Services';
import WhatsAppMixin from '../mixins/WhatsAppMixin';
import HeaderBlock from './elements/HeaderBlock.vue';
import AccountTypeFilter from './elements/AccountTypeFilter.vue';
import DocCategoryCard from './elements/DocCategoryCard.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import RecentDocuments from './elements/RecentDocuments.vue';
import FaqsSection from './FaqsSection.vue';
import WhatsAppPromoCard from './elements/WhatsAppPromoCard.vue';
import WhatsAppEnableDrawer from './elements/WhatsAppEnableDrawer.vue';
import MGWalkthrough from './elements/MGWalkthrough.vue';
import DocumentTypeGrid from './elements/DocumentTypeGrid.vue';

export default {
	mixins: [Services, WhatsAppMixin],
	components: { HeaderBlock, AccountTypeFilter, DocCategoryCard, AiChatBanner, RecentDocuments, FaqsSection, WhatsAppPromoCard, WhatsAppEnableDrawer, MGWalkthrough, DocumentTypeGrid },
	data() {
		return {
			activeTab: 'all',
			documentTypes: null,
			faqs: [],
			heroWalkthroughData: null,
		};
	},
	computed: {
		hasDocuments() {
			return this.documents && this.documents.length > 0;
		},
		filterTabs() {
			const tabs = [{ value: 'all', label: 'All' }];
			if (this.documentTypes) {
				const seen = new Set();
				const categories = [];
				this.documentTypes.forEach(t => {
					const category = t.path?.split(' > ')[1];
					if (category && !seen.has(category)) {
						seen.add(category);
						categories.push(category);
					}
				});
				const counts = this.categoryCounts;
				categories.sort((a, b) => (counts[b] || 0) - (counts[a] || 0) || a.localeCompare(b));
				categories.forEach(c => tabs.push({ value: c, label: c }));
			}
			return tabs;
		},
		categoryCards() {
			const counts = this.categoryCounts;
			const categories = this.filterTabs.filter(t => t.value !== 'all').map(t => t.value);
			const withDocs = categories.filter(name => counts[name]).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
			const withoutDocs = categories.filter(name => !counts[name]);
			return [...withDocs, ...withoutDocs].map(name => ({
				label: name, tabValue: name, illustration: this._getDocumentCategoryImage(name)
			}));
		},
		categoryCounts() {
			if (!this.documents) return {};
			const counts = {};
			this.documents.forEach(doc => {
				const category = this.getDocCategory(doc);
				counts[category] = (counts[category] || 0) + 1;
			});
			return counts;
		},
		groupedDocuments() {
			if (!this.documents) return [];
			const filtered = this.documents.filter(doc => {
				const category = this.getDocCategory(doc);
				return category === this.activeTab;
			});
			const sorted = filtered.sort((a, b) => new Date(b.createDateTime) - new Date(a.createDateTime));
			const groups = [];
			const map = {};
			sorted.forEach(doc => {
				const label = this.getDocGroupLabel(doc);
				if (!map[label]) { map[label] = { label, docs: [] }; groups.push(map[label]); }
				map[label].docs.push(doc);
			});
			return groups;
		},
	},
	watch: {
		activeTab(newVal) {
			if (newVal === 'all') {
				sessionStorage.removeItem('docs-filter-tab');
			} else {
				sessionStorage.setItem('docs-filter-tab', newVal);
			}
		}
	},
	methods: {
		getDocPathParts(doc) {
			const path = doc.sysoDocumentType?.path;
			if (!path) return null;
			return path.split(' > ');
		},
		getDocCategory(doc) {
			const parts = this.getDocPathParts(doc);
			if (!parts || parts.length < 2) return 'Other';
			return parts[1];
		},
		getDocGroupLabel(doc) {
			const parts = this.getDocPathParts(doc);
			return parts && parts.length >= 3 ? parts[2] : doc.sysoDocumentType?.name || doc.documentType || 'Other';
		},
		getCustomer() {
			this._getCustomer(() => {
				this._getDocuments(() => {});
			});
		},
		onLoad() {
			this.getCustomer();
			this._getDocumentTypes((types) => {
				this.documentTypes = types;
			});
			this._getFaqs('document-faqs', (result) => {
				this.faqs = result?.faqItems || [];
			});
			this._getWalkthrough('document-hero', (result) => {
				this.heroWalkthroughData = result;
			});
		},
		properCase(str) {
			if (!str) return '';
			return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
		},
		openDoc(id) {
			this._leavingForDoc = true;
			this.$router.push('/documents/' + id);
		},
		onHeaderWhatsApp() {
			this.activeTab = 'all';
			this.$nextTick(() => {
				if (this.$refs.whatsAppPromoCard?.$el) {
					this.$refs.whatsAppPromoCard.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				this.handleWhatsAppAction();
			});
		},
	},
	mounted() {
		const saved = sessionStorage.getItem('docs-filter-tab');
		if (saved) this.activeTab = saved;
		this.onLoad();
	},
	beforeUnmount() {
		if (!this._leavingForDoc) {
			sessionStorage.removeItem('docs-filter-tab');
		}
	},
};
</script>

<style scoped>
/* Category grid — 2 columns */
.category-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 28px;
}

.whatsapp-promo {
	margin-top: 16px;
}

/* Empty state */
.walkthrough-section {
	margin-bottom: 24px;
}

.what-can-add-heading {
	font-size: 13px;
	margin-top: 24px;
	margin-bottom: 8px;
	text-align: center;
	background: white;
	border-radius: 100px;
	padding: 12px 24px;
	margin-left: auto;
	margin-right: auto;
	width: fit-content;
	position: relative;
}

.what-can-add-heading::after {
	content: '';
	position: absolute;
	bottom: -8px;
	left: 50%;
	transform: translateX(-50%);
	width: 0;
	height: 0;
	border-left: 8px solid transparent;
	border-right: 8px solid transparent;
	border-top: 8px solid white;
}

.what-can-add-subtext {
	font-size: 13px;
	text-align: center;
	margin-top: 18px;
}

.document-type-grid-section {
	margin-top: 24px;
	margin-bottom: 24px;
}

/* Filtered list view */
.no-filtered-docs {
	margin-bottom: 24px;
}

.no-filtered-docs-title {
	font-size: 13px;
	font-weight: bold;
	color: var(--brand-purple);
	margin: 0;
	line-height: 16px;
}

.no-filtered-docs-subtitle {
	font-size: 13px;
	color: var(--brand-purple);
	margin: 0;
	line-height: 16px;
}

.doc-type-group {
	background: rgba(65, 54, 85, 0.05);
	border-radius: 12px;
	padding: 16px 12px 20px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 16px;
}

.group-label {
	font-size: 16px;
	font-weight: bold;
	color: var(--brand-purple);
	margin: 0 0 8px;
	padding: 0 4px;
}

.doc-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 16px;
	background: white;
	border-radius: 8px;
	box-shadow: 0px 4px 8px 0px rgba(51, 40, 71, 0.05);
}

.doc-info {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.doc-info p {
	margin: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.doc-company {
	font-size: 13px;
	font-weight: bold;
	color: var(--brand-purple);
}

.doc-filename {
	font-size: 12px;
	color: var(--brand-purple);
}

.doc-added {
	font-size: 12px;
	color: var(--brand-purple);
	opacity: 0.5;
}

.doc-label {
	font-size: 11px;
	font-weight: bold;
	color: var(--brand-purple);
}

.doc-arrow {
	width: 16px;
	height: 12px;
	flex-shrink: 0;
}

.faq-section {
	border-radius: 8px;
	background-color: white;
}
</style>
